<template>
    <div class="page" v-touch:swipe.left="left">
        <div class="header">
            <link rel="stylesheet" :href="themePath" />
            <Pixi @ready="ready" />
            <Circle top="0" left="0" class="move-1" />
            <Circle right="0" bottom="0" class="move-2"/>
            <EmojiBurst />
        </div>

        <router-view v-slot="{ Component }">
          <keep-alive :include="['music']">
            <component :is="Component" :key="componentKey" />
          </keep-alive>
        </router-view>

        <div class="footer">
            <Sandwich :isFooter="true"
                :threshold="120"
                :dragging="!messengers.isMax"
                ref="playerRef"
                @expand="expanded = true"
                @collapse="expanded = false"
            >
                <template #first-preview>
                    <PlayerPreview @click="expandPlayer"/>
                </template>

                <template #first-detail>
                    <PlayerDetail
                        :collapseButton="messengers.isMax"
                        @collapse="collapsePlayer"/>
                </template>

                <template #second>
                    <Navigation />
                </template>
            </Sandwich>


            <Messages />
            <Modals />
        </div>
    </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, watch, ref } from 'vue';

import Navigation from './components/Navigation/Navigation.vue';
import PlayerPreview from './components/Player/PlayerPreview.vue';
import PlayerDetail from './components/Player/PlayerDetail.vue';
import Sandwich from '🔗/components/ui/Sandwich.vue';
import Circle from '🔗/components/ui/Circle.vue';
import Messages from '🔗/components/ui/Messages.vue';
import Pixi from '🔗/components/ui/Pixi.vue';
import Modals from '@/components/modals/Modals.vue';
import EmojiBurst from '🔗/components/ui/EmojiBurst.vue';

import { storeToRefs } from 'pinia';
import stores from '@/stores';
import { componentKey } from '@/router';
import { keepAwake } from '🔗/utils/utils'

const messengers = stores.messengers();
const others = stores.others();
const { themePath, pageReady } = storeToRefs(others);

const user = stores.user();
const modals = stores.modals();
const nav = stores.nav();

let closeAllQuietCallbackId;
let backCallbackId;

const playerRef = ref(null);
const expanded = ref(false);

const left = (direction, e) => {
    if (e?.target?.closest('.lock-page-swipe')) {
        return;
    }

    if (e.target.closest('body')) {
        nav.back();
    }
}

const ready = async () => {
    if (!stores.messengers().canLogin()) {
        if (nav.page !== 'access-denied') {
            nav.open('username-required');
        }
        return;
    }

    // Login
    if (!user?.token) {
        await user.login();
    }

    if (!user?.token) {
        if (nav.page !== 'access-denied') {
            nav.open('error');
        }
        return;
    }

    if (!user?.profile) {
        await user.me();
    }

    // End we are ready
    setTimeout(() => {
        others.setReady(true);

        if (others.pageReady !== false) {
            close();
        }
    }, 2000);
};

const close = () => {
    modals.close('loader');

    if (!localStorage.welcome) {
        modals.open('welcome');
    }
}

const collapsePlayer = () => {
    if (expanded.value) {
        playerRef.value?.collapse();

        return true;
    }
}

const expandPlayer = () => {
    if (!expanded.value) {
        playerRef.value?.expand();

        return;
    }
}

watch(
    () => pageReady.value,
    async () => {
        if (pageReady.value) {
            close();
        }
    }
);

onMounted(async () => {
    window.addEventListener('touchmove', function (e) {
        if (window.scrollY < 0) {
            e.preventDefault();
        }
    }, { passive: false });

    await messengers.init();

    modals.open('loader');

    document.addEventListener('visibilitychange', keepAwake);

    keepAwake();

    closeAllQuietCallbackId = modals.addCallback({
        type: 'close-all-quiet',
        callback: () => { collapsePlayer() }
    });

    backCallbackId = modals.addCallback({
        type: 'back',
        callback: () => {
            return collapsePlayer()
        }
    });
});

onBeforeUnmount(() => {
    document.removeEventListener('visibilitychange', keepAwake);

    if (closeAllQuietCallbackId) {
        modals.removeCallback(closeAllQuietCallbackId);
        closeAllQuietCallbackId = null;
    }

    if (backCallbackId) {
        modals.removeCallback(backCallbackId);
        backCallbackId = null;
    }
});
</script>


<style scoped lang="scss">
    .header {
        margin-bottom: calc(-1 * var(--block-gap));
    }

    .footer {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        width: 100%;
        z-index: 1;
    }
</style>
